import sampleBuilders from '../../../../contracts/native-sportsbook/v1/parlay-builders.json' with { type: 'json' }
import { columnParam, nextPlayerPropColumn } from './playerPropBoardModel.js'

const MILESTONE_PREFIX_PATTERN = /^\d+\+\s*/

// Mirrors the native apps and the runtime (D-0046): a league lists the games the
// provider offers for same-game parlays; a milestone group is a player prop grid
// of provider criteria, and a game line is one provider criterion's main line.

export const BUILDER_LIMITS = Object.freeze({ leagues: 6, groups: 4, columns: 5, lines: 3, events: 8 })
export const BUILDER_EVENT_LIMITS = [1, 2, 3, 4, 5, 6, 7, 8]

/** The tab a game line reads as by default, from the outcome shape the runtime reports. */
export const LINE_SHAPE_LABELS = Object.freeze({ team_line: 'Spreads', over_under: 'Totals', team: 'Moneyline' })

const ladder = (criterionId, lines) => lines.map((line) => ({ label: `${line / 1000}+`, criterion_id: criterionId, selection: 'yes', line_millis: line }))

const BASKETBALL_LINES = [
  { label: 'Spreads', criterion_id: '1001159512', shape: 'team_line' },
  { label: 'Totals', criterion_id: '1001159509', shape: 'over_under' },
  { label: 'Moneyline', criterion_id: '1001159732', shape: 'team' },
]

// Provider criteria observed on the Kambi production Offering on 2026-10-08,
// exactly tooling/fixtures/v1/parlay-builder-widget.json, so a new builder's
// requests are the canonical sample builders. Discovery under live data offers
// whatever the games post.
export const PARLAY_BUILDER_TEMPLATES = [
  { key: 'nfl', sport: 'american_football', competition: 'NFL', label: 'NFL',
    milestones: { label: 'Player Milestones', groups: [
      { key: 'nfl_passing', label: 'Passing', header: 'PASS YARDS', columns: ladder('2100057916', [175000, 200000, 225000, 250000, 275000]) },
      { key: 'nfl_rushing', label: 'Rushing', header: 'RUSH YARDS', columns: ladder('2100057918', [40000, 50000, 60000, 70000, 80000]) },
      { key: 'nfl_receiving', label: 'Receiving', header: 'REC YARDS', columns: ladder('2100057917', [40000, 50000, 60000, 70000, 80000]) },
      { key: 'nfl_touchdowns', label: 'TDs', header: 'TOUCHDOWNS', columns: [
        { label: 'Anytime', criterion_id: '1002231004', selection: 'yes', line_millis: 1000 },
        { label: '2+', criterion_id: '1006316175', selection: 'yes', line_millis: 2000 },
        { label: '3+', criterion_id: '1006316175', selection: 'yes', line_millis: 3000 },
      ] },
    ] },
    game_lines: [
      { label: 'Spreads', criterion_id: '1001159928', shape: 'team_line' },
      { label: 'Totals', criterion_id: '1001159490', shape: 'over_under' },
      { label: 'Moneyline', criterion_id: '1004709447', shape: 'team' },
    ] },
  { key: 'nba', sport: 'basketball', competition: 'NBA', label: 'NBA', game_lines: BASKETBALL_LINES },
  { key: 'wnba', sport: 'basketball', competition: 'WNBA', label: 'WNBA', game_lines: BASKETBALL_LINES },
]

const newId = () => globalThis.crypto.randomUUID()
const sameLeague = (left, right) => left?.sport === right?.sport && (left?.competition || '').toLowerCase() === (right?.competition || '').toLowerCase()

export function builderTemplateFor(sport, competition) {
  return PARLAY_BUILDER_TEMPLATES.find((template) => sameLeague(template, { sport, competition })) || null
}

/** A group's authored shape with fresh identities for the group and its columns. */
export function groupFromTemplate(template, createUUID = newId) {
  return { id: createUUID(), label: template.label, header: template.header, columns: template.columns.map(({ label, criterion_id, selection, line_millis }) => ({ id: createUUID(), label, criterion_id, selection, ...(line_millis === undefined ? {} : { line_millis }) })) }
}

/**
 * A league for the given competition: the observed template when there is one,
 * otherwise a league with no markets yet, which Studio validation flags until
 * the operator picks posted markets.
 */
export function leagueFor(sport, competition, createUUID = newId, id = createUUID()) {
  const template = builderTemplateFor(sport, competition)
  const league = { id, label: (template?.label || competition || '').slice(0, 12), sport, competition: template?.competition || competition, event_limit: 6 }
  if (template?.milestones) league.milestones = { label: template.milestones.label, groups: template.milestones.groups.map((group) => groupFromTemplate(group, createUUID)) }
  if (template?.game_lines?.length) league.game_lines = template.game_lines.map((line) => ({ id: createUUID(), label: line.label, criterion_id: line.criterion_id }))
  return league
}

export function groupsParam(league) {
  return (league?.milestones?.groups || []).map((group) => (group.columns || []).map(columnParam).join(',')).join(';')
}

export function linesParam(league) {
  return (league?.game_lines || []).map((line) => line.criterion_id).join(',')
}

/** The runtime builder request for one league, in the shape the native apps send. */
export function builderQuery(component, league, eventId) {
  const query = { sport: league?.sport, competition: league?.competition }
  if (eventId) query.event_id = eventId
  return { ...query, events: league?.event_limit, players: component?.player_limit, groups: groupsParam(league), lines: linesParam(league) }
}

export function builderQueryComplete(query) {
  return Boolean(query.sport && query.competition && query.events && query.players && (query.groups || query.lines))
}

/** The Studio API parameters for a builder query: empty groups and lines are omitted, as the apps do. */
export function builderRequestParams(query) {
  const params = { ...query }
  if (!params.groups) delete params.groups
  if (!params.lines) delete params.lines
  return params
}

/** Sample games answer only their canonical requests, exactly as the apps' fixture repository does. */
export function sampleBuilder(query) {
  const entry = sampleBuilders.builders.find(({ request }) => request.sport === query.sport &&
    request.competition.toLowerCase() === (query.competition || '').toLowerCase() &&
    (request.event_id || undefined) === (query.event_id || undefined) &&
    request.events === Number(query.events) && request.players === Number(query.players) &&
    request.groups === (query.groups || '') && request.lines === (query.lines || ''))
  return entry?.builder || null
}

/** Every authored object with an instance identity, the component first, as the server walks them. */
export function builderIdentities(component) {
  const leagues = Array.isArray(component?.leagues) ? component.leagues : []
  const groups = leagues.flatMap((league) => league?.milestones?.groups ?? [])
  return [
    ...leagues,
    ...leagues.flatMap((league) => league?.game_lines ?? []),
    ...groups,
    ...groups.flatMap((group) => group?.columns ?? []),
  ]
}

/** A copied builder gets fresh identities for every league, group, column and game line. */
export function reidentifyBuilder(component, uuid) {
  for (const item of builderIdentities(component)) item.id = uuid()
}

/** Whether every league is the hub's league, compared as the server's hub check does. */
export function builderFitsHub(component, context) {
  return (component?.leagues || []).every((league) => sameLeague(league, context))
}

const columnKey = (column) => `${column.criterion_id}:${column.selection}:${column.line_millis ?? ''}`

/** Ladder groups built only from posted player markets: up to five consecutive posted lines around the middle. */
export function groupSuggestionsFromMarkets(markets = [], templates = []) {
  const offered = new Set(templates.map((template) => template.columns.map(columnKey).join(',')))
  const suggestions = []
  for (const market of markets) {
    if (market.offer_shape !== 'player_line' || !market.selections?.includes('yes') || (market.lines?.length ?? 0) < 2) continue
    const start = Math.max(0, Math.floor((market.lines.length - BUILDER_LIMITS.columns) / 2))
    const lines = market.lines.slice(start, start + BUILDER_LIMITS.columns)
    const name = (market.short_label || market.label).replace(MILESTONE_PREFIX_PATTERN, '')
    const columns = lines.map((line) => ({ label: `${line.line_millis / 1000}+`, criterion_id: market.criterion_id, selection: 'yes', line_millis: line.line_millis }))
    const key = columns.map(columnKey).join(',')
    if (offered.has(key)) continue
    offered.add(key)
    suggestions.push({ key: `ladder:${market.criterion_id}`, name: `${name} milestones`, label: name.slice(0, 16), header: name.toUpperCase().slice(0, 24), columns })
  }
  return suggestions
}

/**
 * A stat from posted player markets when no ladder applies, such as an over/under
 * pick or a single yes line: the first posted column the builder does not show yet,
 * named by its provider market. Null when every posted column is already shown.
 */
export function groupFromPostedMarkets(usedColumns, markets, createUUID = newId) {
  const column = nextPlayerPropColumn(usedColumns, markets)
  if (!column) return null
  const market = markets.find((candidate) => candidate.criterion_id === column.criterion_id)
  const name = (market?.short_label || market?.label || 'Player props').replace(MILESTONE_PREFIX_PATTERN, '')
  const label = column.selection === 'yes' && column.line_millis != null ? `${column.line_millis / 1000}+` : column.label
  return { id: createUUID(), label: name.slice(0, 16), header: name.toUpperCase().slice(0, 24), columns: [{ id: createUUID(), ...column, label: label.slice(0, 12) }] }
}

/** Game-line choices: the posted lines under live data, the league template's otherwise. */
export function lineChoices(discovered, sport, competition) {
  if (discovered?.length) return discovered
  return (builderTemplateFor(sport, competition)?.game_lines || []).map((line) => ({ criterion_id: line.criterion_id, label: `${line.label} (${line.criterion_id})`, shape: line.shape, main_line: true }))
}

/** The game line a new tab adds: the first choice the league does not show yet, main lines first. */
export function nextGameLine(lines, choices) {
  const used = new Set((lines || []).map((line) => line.criterion_id))
  const ordered = [...choices.filter((choice) => choice.main_line), ...choices.filter((choice) => !choice.main_line)]
  const choice = ordered.find((candidate) => !used.has(candidate.criterion_id))
  return choice ? { label: (LINE_SHAPE_LABELS[choice.shape] || 'Lines').slice(0, 16), criterion_id: choice.criterion_id } : null
}

/** Duplicate leagues, columns within a group and line criteria within a league, as the server rejects them. */
export function builderAuthoringIssues(component) {
  const issues = []
  const leagues = component?.leagues || []
  const seenLeagues = new Set()
  leagues.forEach((league, index) => {
    const key = `${league.sport}|${(league.competition || '').toLowerCase()}`
    if (seenLeagues.has(key)) issues.push({ league: index, message: `${league.competition} is already a league in this builder.` })
    seenLeagues.add(key)
    if (!league.milestones && !league.game_lines?.length) issues.push({ league: index, message: 'Add player milestones or a game line.' })
    for (const group of league.milestones?.groups || []) {
      const keys = (group.columns || []).map(columnKey)
      if (new Set(keys).size !== keys.length) issues.push({ league: index, message: `${group.label || 'A group'} repeats a market and line.` })
    }
    const criteria = (league.game_lines || []).map((line) => line.criterion_id)
    if (new Set(criteria).size !== criteria.length) issues.push({ league: index, message: 'Each game line must be a different market.' })
  })
  return issues
}

// MARK: Preview

/** Tabs in the apps' order: milestones when any group posts a player, then each posted line. */
export function availableTabs(builder) {
  if (!builder || builder.state !== 'ready') return []
  const tabs = builder.groups.some((group) => group.rows.length) ? ['milestones'] : []
  return [...tabs, ...builder.game_lines.flatMap((line, index) => (line ? [`line:${index}`] : []))]
}

/** The caption convention a line's outcome types call for, never its label (ParlayBuilderGameLine.presentationType). */
export function linePresentation(line) {
  if (line.outcomes.some((outcome) => outcome.type === 'OT_OVER' || outcome.type === 'OT_UNDER')) return 'total'
  return line.outcomes.some((outcome) => outcome.line_millis != null) ? 'spread' : 'moneyline'
}

/** Team sides read away then home, as the game chip does, and over before under. */
export function orderedOutcomes(line, event) {
  const away = event?.participants.find((participant) => participant.role === 'away')?.id
  const home = event?.participants.find((participant) => participant.role === 'home')?.id
  const rank = (outcome) => outcome.participant_id ? (outcome.participant_id === away ? 0 : outcome.participant_id === home ? 1 : 2)
    : outcome.type === 'OT_OVER' ? 0 : outcome.type === 'OT_UNDER' ? 1 : 2
  return line.outcomes.map((outcome, index) => ({ outcome, index })).sort((left, right) => rank(left.outcome) - rank(right.outcome) || left.index - right.index).map(({ outcome }) => outcome)
}

/** The line a game-line price carries: signed for a spread, unsigned for a total. */
export function lineCaption(outcome, presentation) {
  if (outcome.line_millis == null || presentation === 'moneyline') return null
  const value = outcome.line_millis / 1000
  if (presentation === 'total') return String(Math.abs(value))
  return value > 0 ? `+${value}` : String(value)
}

export function kickoffLabel(iso, locale = 'en-US', timeZone) {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString(locale, { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZone })
}
